/*
 * Description:
 * ---------------------
 * Passkeys in the browser, for approvers (ApproversPanel, ApprovalPage). The
 * server's WebAuthn routes hand their options over as go-webauthn writes them,
 * every binary field in base64url; these turn them into what
 * navigator.credentials takes, and a credential back into the JSON the
 * server's finish routes parse, the WebAuthn Level 3 JSON.
 *
 * And the challenge an approver's passkey signs (the M4 contract, section 1):
 * SHA-256 over the kind's context, the digest of what is approved and the
 * digest of its rendering. The approval page works out the rendering's digest
 * from the rows it shows and asks the passkey only when the server's
 * challenge is the one over it, so what a person reads is what they sign.
 *
 * ! file components/passkeys.js
 */

/* Why this page cannot make or use a passkey, "" when it can: a passkey needs
   a page of its own (a box refuses an assertion made inside another site's
   frame, and a frame is how a yes is tricked out of someone), a secure
   address (https, or localhost while developing) and a browser with
   WebAuthn. */
export const passkeyBlocker = () => (window.self !== window.top ? "framed" : !window.isSecureContext ? "insecure"
  : !window.PublicKeyCredential || !navigator.credentials ? "unsupported" : "");

const fromB64u = (s) => {
  const t = String(s).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(t.padEnd(Math.ceil(t.length / 4) * 4, "=")), (c) => c.charCodeAt(0));
};
const toB64u = (buf) => {
  let s = "";
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const publicKey = (json) => json?.publicKey || json;
const ids = (list) => (list || []).map((c) => ({ ...c, id: fromB64u(c.id) }));

/* creationOptions is a registration's options, from the server's begin route. */
export function creationOptions(json) {
  const o = publicKey(json);
  return { ...o, challenge: fromB64u(o.challenge), user: { ...o.user, id: fromB64u(o.user.id) }, excludeCredentials: ids(o.excludeCredentials) };
}

/* requestOptions is an approval's options, from the server's begin route.
   Every box refuses an approval whose authenticator did not verify its
   holder, so the page asks for that whatever the server said. */
export function requestOptions(json) {
  const o = publicKey(json);
  return { ...o, challenge: fromB64u(o.challenge), allowCredentials: ids(o.allowCredentials), userVerification: "required" };
}

/* credentialJSON is a credential the browser made or used, as the server's
   finish routes read it. A new passkey also carries its key as DER
   SubjectPublicKeyInfo and its COSE algorithm, which is what an approver
   list holds and every box checks with. */
export function credentialJSON(cred) {
  const r = cred.response;
  const made = !!r.attestationObject;
  const key = made && r.getPublicKey ? r.getPublicKey() : null;
  const response = made
    ? {
      clientDataJSON: toB64u(r.clientDataJSON),
      attestationObject: toB64u(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
      ...(r.getAuthenticatorData ? { authenticatorData: toB64u(r.getAuthenticatorData()) } : {}),
      ...(key ? { publicKey: toB64u(key), publicKeyAlgorithm: r.getPublicKeyAlgorithm() } : {}),
    }
    : {
      clientDataJSON: toB64u(r.clientDataJSON),
      authenticatorData: toB64u(r.authenticatorData),
      signature: toB64u(r.signature),
      ...(r.userHandle ? { userHandle: toB64u(r.userHandle) } : {}),
    };
  return {
    id: cred.id,
    rawId: toB64u(cred.rawId),
    type: cred.type,
    ...(cred.authenticatorAttachment ? { authenticatorAttachment: cred.authenticatorAttachment } : {}),
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response,
  };
}

const hex = (bytes) => [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
export const sameBytes = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

async function sha256(text) {
  if (!window.crypto?.subtle) throw new Error("insecure");
  return new Uint8Array(await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
}

/* What each kind of approval signs over, before its digests (sections 1 and
   2 of the contract): a key card, an approver list, a rules bundle. */
const PREFIX = { mandate: "nabx-approval-1", approvers: "nabx-approvers-1", rules: "nabx-rules-1" };

/* shownOf is the digest of a rendering exactly as shown, written as the box
   and the server write it (authority.Shown): "sha256:<hex>". */
export const shownOf = async (rendering) => `sha256:${hex(await sha256(rendering))}`;

/* challengeOf is what a passkey signs to approve the statement whose digest
   is digest, shown the rendering whose digest is shown (authority.Challenge,
   ApproversChallenge, RulesChallenge). */
export const challengeOf = (kind, digest, shown) => sha256(`${PREFIX[kind] || PREFIX.mandate}\n${digest}\n${shown}`);
